import React, { useCallback, useEffect, useMemo, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';

import { StyledText } from '../../atoms/Text/StyledText';
import { StyledUserPanelAllEvents } from './UserPanelAllEvents.styles';
import {
    StyledGlobalHeadingWrapper,
    StyledGlobalScrollWrapper,
    StyledLoaderWrapper,
} from '../../../styles/sharedStyles';
import UserPanelHeadingText from '../../atoms/UserPanel/UserPanelHeadingText';
import useScrollBar from '../../../logic/hooks/useScrollBar';
import Loader from '../Loader/Loader';
import UserPanelTournament from '../UserPanelTournament/UserPanelTournament';
import { getEvents } from '../../../logic/requests/events.js';
import { getString } from '../../../strings';
import { useInfiniteQuery } from '@tanstack/react-query';

const UserPanelAllEvents = ({ user }) => {
    const { scrollElement, isScrollBar } = useScrollBar();

    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError } =
        useInfiniteQuery(
            ['allEvents'],
            async ({ pageParam = 0 }) => {
                const res = await getEvents({ cursorId: pageParam });

                return {
                    data: Array.isArray(res) ? res : [],
                    nextCursor:
                        Array.isArray(res) && res.length ? res[res.length - 1].id : undefined,
                };
            },
            {
                getNextPageParam: (lastPage) => lastPage.nextCursor,
            }
        );

    const allEvents = useMemo(() => data?.pages.flatMap((page) => page.data) ?? [], [data]);

    const { retAllEvents, retOverEvents } = useMemo(() => {
        const active = allEvents.filter((event) => event.status !== 'over');
        const over = allEvents.filter((event) => event.status === 'over');

        return { retAllEvents: active, retOverEvents: over };
    }, [allEvents]);

    return (
        <StyledUserPanelAllEvents>
            {!isError.message ? (
                <>
                    <StyledGlobalHeadingWrapper>
                        <UserPanelHeadingText
                            text={`${getString('User_panel_all_events_wszystkie_zawody')}:`}
                        />
                    </StyledGlobalHeadingWrapper>
                    <StyledGlobalScrollWrapper $isscrollbar={isScrollBar} ref={scrollElement}>
                        {!allEvents ? (
                            <StyledLoaderWrapper>
                                <Loader />
                            </StyledLoaderWrapper>
                        ) : (
                            <InfiniteScroll
                                loadMore={() => {
                                    if (!isFetchingNextPage && hasNextPage) {
                                        fetchNextPage();
                                    }
                                }}
                                hasMore={hasNextPage}
                                initialLoad={false}
                                getScrollParent={() => scrollElement.current}
                                useWindow={!isScrollBar}
                            >
                                {retAllEvents?.map((event) => (
                                    <UserPanelTournament
                                        key={event.id}
                                        event={event}
                                        user={user}
                                        allUsers
                                    />
                                ))}
                                <StyledText
                                    hasdeclaredtexttransform="uppercase"
                                    hasdeclaredfontsize="54px"
                                    hasdeclaredpadding={
                                        retAllEvents?.length
                                            ? '32px 20px 32px 20px'
                                            : '0 20px 32px 20px'
                                    }
                                    hasdeclaredfontweight="700"
                                    hasdeclaredtextalign="center"
                                >
                                    {getString('pages_userPanel_minione')}
                                </StyledText>
                                {retOverEvents?.map((event) => (
                                    <UserPanelTournament
                                        key={event.id}
                                        event={event}
                                        user={user}
                                        allUsers
                                    />
                                ))}
                            </InfiniteScroll>
                        )}
                    </StyledGlobalScrollWrapper>
                </>
            ) : (
                <StyledLoaderWrapper>
                    <StyledText
                        hasdeclaredtexttransform="uppercase"
                        hasdeclaredpadding="0 20px 0 20px"
                        hasdeclaredfontsize="46px"
                        hasdeclaredfontweight="700"
                        hasdeclaredtextalign="center"
                        hasdeclaredmaxwidth="600px"
                        hasdeclaredlineheight="1.4em"
                        as="h2"
                    >
                        {isError.message}
                    </StyledText>
                </StyledLoaderWrapper>
            )}
        </StyledUserPanelAllEvents>
    );
};

export default UserPanelAllEvents;
